import { UserState } from '../../../features/account/services/user.state';
import {
  Component,
  computed,
  ElementRef,
  inject,
  input,
  inputBinding,
  signal,
  ViewChild,
} from '@angular/core';
import { Avatar } from '../../../shared/components/avatar/avatar';
import { Viewport } from '../../../core/services/viewport';
import { LOCAL_ROUTES } from '../../../core/constants/local-routes.constants';
import { computeClasses } from '../../../core/utils/class';
import { NgClass } from '@angular/common';
import { GalleryOutline } from '../../../shared/components/icons/gallery-outline';
import { EditTwoOutline } from '../../../shared/components/icons/edit-two-outline';
import { PrimaryButton } from '../../../shared/components/buttons/primary-button/primary-button';
import { Dialog } from '../../../core/services/dialog';
import { finalize } from 'rxjs';

@Component({
  selector: 'user-header',
  imports: [Avatar, PrimaryButton, GalleryOutline, NgClass],
  templateUrl: './user-header.html',
  styleUrl: './user-header.scss',
})
export class UserHeader {
  public userState = inject(UserState);
  private dialog = inject(Dialog);
  private viewport = inject(Viewport);
  @ViewChild('avatarFileInput')
  avatarFileInput?: ElementRef<HTMLInputElement>;

  showEditProfile = input<boolean>(false);
  EditTwoOutline = EditTwoOutline;
  uploadingImage = signal(false);

  routes = LOCAL_ROUTES;

  classes = computed(() => {
    return computeClasses('user-header', {
      'with-edit-profile': this.showEditProfile(),
    });
  });

  userFullName = computed(() => {
    const user = this.userState.user();
    if (!user) return '';
    return `${user.usuario_nombres} ${user.usuario_apellidos}`;
  });

  avatarSize = computed(() => {
    switch (true) {
      case this.viewport.isXs():
      case this.viewport.isSm():
        return '3xl' as const;
      case this.viewport.isMd():
        return '4xl' as const;
      default:
        return '5xl' as const;
    }
  });

  onAvatarClick(): void {
    if (this.uploadingImage()) return;
    this.avatarFileInput?.nativeElement.click();
  }

  onAvatarKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    this.onAvatarClick();
  }

  async onAvatarFileChange(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;

    if (!this.validateImage(file)) return;

    const { AvatarCropModal } =
      await import('../../../shared/components/modals/avatar-crop-modal/avatar-crop-modal');

    this.dialog
      .open(AvatarCropModal, [inputBinding('imageFile', () => file)])
      .subscribe(croppedImage => {
        if (!croppedImage) return;
        const renamedImage = this.renameAvatarFile(croppedImage);
        this.uploadingImage.set(true);
        this.userState
          .updateImage(renamedImage)
          .pipe(finalize(() => this.uploadingImage.set(false)))
          .subscribe({
            error: () => {
              this.dialog.toast({
                variant: 'danger',
                label: 'No se pudo actualizar la imagen',
                message: 'Intenta nuevamente en unos segundos.',
              });
            },
          });
      });
  }

  private validateImage(file: File): boolean {
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];

    if (!validTypes.includes(file.type)) {
      this.dialog.toast({
        variant: 'warning',
        label: 'Formato no permitido',
        message: 'Solo se aceptan imágenes JPG, PNG o WEBP.',
      });
      return false;
    }

    return true;
  }

  private renameAvatarFile(file: File): File {
    const userId = this.userState.user()?.usuario_id ?? 'unknown';
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    const timestamp = `${year}${month}${day}-${hours}${minutes}${seconds}`;
    return new File([file], `avatar-${userId}-${timestamp}.jpg`, {
      type: file.type || 'image/jpeg',
    });
  }
}
